import type {RefObject} from 'react'
import {useLayoutEffect} from 'react'

import {SidebarResizeHandle} from './SidebarResizeHandle'
import {type ExpandedWidth, useResizableSidebarWidth} from './use-resizable-sidebar-width'

export interface SidebarResizeControllerProps {
  /** The sidebar element whose `--sidebar-width` custom property is driven. */
  sidebarRef: RefObject<HTMLDivElement | null>
  /** The drag handle (slider) node, shared with `useResizableSidebarWidth`. */
  handleRef: RefObject<HTMLDivElement | null>
  expandedWidth: ExpandedWidth
  minWidth: number
  maxWidth: number
  widthStorageKey?: string
  initialWidth?: number
  onResizeEnd?: (width: number) => void
  /**
   * Reports the committed width up to the parent so its declarative
   * `--sidebar-width` stays in sync with the persisted value — after a drag and
   * across collapse/re-expand. Must be a stable reference (e.g. a `useState`
   * setter).
   */
  onWidthChange: (width: number) => void
}

/**
 * Owns the resize width behavior for a {@link CollapsibleSidebar}'s expanded rail.
 *
 * Rendered only when the drag handle is actually shown (`resizable`, expanded, and
 * not inside the narrow-viewport Dialog), so `useResizableSidebarWidth`'s window
 * resize listener and width-cookie subscription are scoped to that case instead of
 * running for every `CollapsibleSidebar` instance. Kept as a child of the stable
 * sidebar wrapper (rather than owning the wrapper element) so mounting/unmounting
 * on expand/collapse never remounts the sidebar and breaks its width transition.
 */
export function SidebarResizeController({
  sidebarRef,
  handleRef,
  expandedWidth,
  minWidth,
  maxWidth,
  widthStorageKey,
  initialWidth,
  onResizeEnd,
  onWidthChange,
}: SidebarResizeControllerProps) {
  const {currentWidth, currentWidthRef, maxPaneWidth, getMaxPaneWidth, saveWidth, getDefaultWidth} =
    useResizableSidebarWidth({
      expandedWidth,
      minWidth,
      maxWidth,
      storageKey: widthStorageKey,
      initialWidth,
      onResizeEnd,
      paneRef: sidebarRef,
      handleRef,
    })

  // Sync the committed width up so the parent's declarative `--sidebar-width`
  // matches the persisted cookie value, including after a drag and across
  // collapse/re-expand. Runs before paint to avoid a width flash on mount.
  useLayoutEffect(() => {
    onWidthChange(currentWidth)
  }, [currentWidth, onWidthChange])

  return (
    <SidebarResizeHandle
      sidebarRef={sidebarRef}
      handleRef={handleRef}
      minWidth={minWidth}
      maxWidth={maxPaneWidth}
      getMaxWidth={getMaxPaneWidth}
      currentWidth={Math.min(currentWidth, maxPaneWidth)}
      currentWidthRef={currentWidthRef}
      onResizeEnd={saveWidth}
      onReset={getDefaultWidth}
    />
  )
}
